import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ServiceBase } from "../../shared";
import {
  AdequacyBreakdownData,
  TopCountiesByGapData,
  ExceptionsData,
  TopSpecialtiesByGapData,
  CompositeFillableGapsData,
  LineOfBusinessData,
  GapOverviewData,
  EcdApiFilters,
  EcdTrendingArrowEnum,
  CountyStatusData,
  GapsByAgeData,
  EcdWidgetData,
  GapCountDrillInModel,
  CompositeFillableGapCountDrillInModel,
  AdequacyBreakdownDrillInModel,
  ExceptionsDrillInModel,
} from "./interfaces";

export default class EnterpriseComplianceDashboardService extends ServiceBase {
  /**
   * Filters methods to retrieve filter options
   */
  public async getLOBFilterOptions(): Promise<DropdownItem<string>[]> {
    return await this.makeGet<DropdownItem<string>[]>("enterprisecompliancedashboard/lob-filters");
  }

  public async getServiceAreaOptions(): Promise<NestingParentDropdownItem[]> {
    return await this.makeGet<NestingParentDropdownItem[]>("enterprisecompliancedashboard/region-filters");
  }

  public async getSpecialtyFilterOptions(): Promise<DropdownItem<string>[]> {
    return await this.makeGet<DropdownItem<string>[]>("enterprisecompliancedashboard/specialty-group-filters");
  }

  public async getCountyStatusFilterOptions(): Promise<DropdownItem<string>[]> {
    return await this.makeGet<DropdownItem<string>[]>("enterprisecompliancedashboard/county-status-filters");
  }

  public async getProjectsFilterOptions(): Promise<DropdownItem<string>[]> {
    return await this.makeGet<DropdownItem<string>[]>("enterprisecompliancedashboard/project-name-filters");
  }

  public createDropdownItems = (records: Record<string, string>): Array<DropdownItem<string>> => {
    const entries = Object.entries(records);
    return entries.map(([key, value]) => {
      return {
        key,
        name: value,
      };
    });
  }

  /**
   * Dashboard methods to retrieve real data
   */
  public async getWidgetData(requestModel: EcdApiFilters): Promise<EcdWidgetData> {
    return this.makePost("enterprisecompliancedashboard/widget-data", requestModel);
  }

  public async getGapOverviewData(filters: EcdApiFilters): Promise<GapOverviewData[]> {
    return await this.makePost("enterprisecompliancedashboard/gap-overview", filters);
  }

  public async getCompositeFillableGapsData(requestModel: EcdApiFilters): Promise<CompositeFillableGapsData> {
    return this.makePost("enterprisecompliancedashboard/composite-fillable-gaps", requestModel);
  }

  public async getGapsByAgeData(filters: EcdApiFilters): Promise<GapsByAgeData[]> {
    return await this.makePost<GapsByAgeData[]>("enterprisecompliancedashboard/gaps-by-age", filters);
  }

  public async getGapsBySpecialtyData(requestModel: EcdApiFilters): Promise<TopSpecialtiesByGapData[]> {
    return this.makePost("enterprisecompliancedashboard/gaps-by-specialty-group", requestModel);
  }

  public async getTopCountiesByGapData(requestModel: EcdApiFilters): Promise<TopCountiesByGapData[]> {
    return this.makePost("enterprisecompliancedashboard/counties-by-gap-count", requestModel);
  }

  public async getTopCountiesByGapDrillInData(requestModel: EcdApiFilters): Promise<GapCountDrillInModel[]> {
    return this.makePost("enterprisecompliancedashboard/gap-count-by-county-drill-in", requestModel);
  }

  public async getLineOfBusinessData(requestModel: EcdApiFilters): Promise<LineOfBusinessData[]> {
    return this.makePost("enterprisecompliancedashboard/network-taxonomy-lob-overview", requestModel);
  }

  public async getCountyStatusData(requestModel: EcdApiFilters): Promise<CountyStatusData[]> {
    return this.makePost("enterprisecompliancedashboard/county-status-overview", requestModel);
  }


  public async getExceptionsData(requestModel: EcdApiFilters): Promise<ExceptionsData[]> {
    return this.makePost("enterprisecompliancedashboard/exceptions", requestModel);
  }

  public async getSpecialtiesByGapDrillInData(requestModel: EcdApiFilters): Promise<GapCountDrillInModel[]> {
    return this.makePost("enterprisecompliancedashboard/gap-count-by-specialty-drill-in", requestModel);
  }

  public async getCompositeFillableGapsDrillInData(requestModel: EcdApiFilters): Promise<CompositeFillableGapCountDrillInModel[]> {
    return this.makePost("enterprisecompliancedashboard/composite-fillable-gaps-drill-in", requestModel);
  }

  public async getExceptionsDrillInData(requestModel: EcdApiFilters): Promise<ExceptionsDrillInModel[]> {
    return this.makePost("enterprisecompliancedashboard/exceptions-drill-in", requestModel);
  }
    
  public async getAdequacyBreakdownDrillInData(requestModel: EcdApiFilters): Promise<AdequacyBreakdownDrillInModel[]> {
    return this.makePost("enterprisecompliancedashboard/adequacy-breakdown-drill-in", requestModel);
  }

  /**
   * Utility methods
   */

  public async queueEcdJobs(): Promise<void> {
    return await this.makePost("enterprisecompliancedashboard/queue-ecd-jobs");
  }

  public async purgeDemoData(): Promise<void> {
    return await this.makePost("enterprisecompliancedashboard/purge-demo-data");
  }

  /**
   * Dashboard methods to present empty data, in case the filters return nothing
   */

  public async getEmptyWidgetData(): Promise<EcdWidgetData> {
    const emptyData: EcdWidgetData = {
      adequacyBreakdownDataModel: (await this.getEmptyAdequacyBreakdownData()),
      countyStatusOverviewModels: await this.getEmptyCountyStatusData(),
      gapCountByAgeModels: await this.getEmptyGapsByAgeData(),
      gapOverviewDataModels: await this.getEmptyGapOverviewData(),
      networkTaxonomyLineOfBusinessOverviewModels: await this.getEmptyLineOfBusinessData(),
    };
    return emptyData;
  }

  public async getEmptyAdequacyBreakdownData(): Promise<AdequacyBreakdownData> {
    return {
      "closedGaps": null,
      "countySpecialtyGroupCount": null,
      "countySpecialtyGroupCountPrior": null,
      "countySpecialtyGroupCountTrend": EcdTrendingArrowEnum.Unknown,
      "gapPercent": null,
      "gapPercentPrior": null,
      "gapPercentTrend": EcdTrendingArrowEnum.Unknown,
      "medianGapAge": null,
      "medianGapAgePrior": null,
      "medianGapAgeTrend": EcdTrendingArrowEnum.Unknown,
      "newGaps": null,
      "overallGapCount": null,
      "overallGapCountPrior": null,
      "overallGapCountTrend": EcdTrendingArrowEnum.Unknown,
    }
  }

  public async getEmptyGapOverviewData(): Promise<GapOverviewData[]> {
    return [
      {
        gapCount: 0,
        week: null,
        weekEnd: null,
      },
    ];
  }

  public async getEmptyCompositeFillableGapsData(): Promise<CompositeFillableGapsData> {
    return {
      fillableByOneGapCount: 0,
      fillableByOnePercentage: 0,
      fillableGapCount: 0,
      fillablePercentage: 0,
      gapCount: 0,
    };
  }

  public async getEmptyGapsByAgeData(): Promise<GapsByAgeData[]> {
    return [
      {
        gapAge30: 0,
        gapAge60: 0,
        gapAge90: 0,
        gapAgeOver90: 0,
        month: "",
        percentOfTotalGaps30: 0,
        percentOfTotalGaps60: 0,
        percentOfTotalGaps90: 0,
        percentOfTotalGapsOver90: 0,
      },
    ];
  }

  public async getEmptyGapsBySpecialtyData(): Promise<TopSpecialtiesByGapData[]> {
    return [
      {
        fillableGapCount: 0,
        specialtyGroupCode: "",
        specialtyGroupDisplayName: "",
        specialtyGroupId: 0,
        unfillableGapCount: 0,
      },
    ]
  }

  public async getEmptyExceptionsData(): Promise<ExceptionsData[]> {
    return [
      {
        exceptionsAppealed: 0,
        exceptionsApproved: 0,
        exceptionsDenied: 0,
        exceptionsPending: 0,
        exceptionsSubmitted: 0,
        month: "",
      },
    ]
  }

  public async getEmptyTopCountiesByGapData(): Promise<TopCountiesByGapData[]> {
    return [
      {
        canonicalRegionSetId: 0,
        countyName: "---",
        countySSA: "---",
        gaps: "---",
        projects: "---",
      },
    ]
  }

  public async getEmptyLineOfBusinessData(): Promise<LineOfBusinessData[]> {
    return [
      {
        approvedExceptionCount: "---",
        compositeFillableGapCount: "---",
        countyCount: "---",
        gapCount: "---",
        gapPercent: "---",
        medianGapAge: "---",
        networkTaxonomyLOB: "Total",
      }
    ];
  }

  public async getEmptyCountyStatusData(): Promise<CountyStatusData[]> {
    return [
      {
        compositeFillableGapCount: "---",
        countyCount: "---",
        countyStatus: "Total",
        gapCount: "---",
        gapPercent: "---",
        medianGapAge: "---",
      },
    ];
  }
}
